import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { DashboardLayout } from '../components/layouts/DashboardLayout';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { ProgressRing } from '../components/ui/ProgressRing';
import { PreTradeChecklist } from '../components/ui/PreTradeChecklist';
import { PostTradeChecklist } from '../components/ui/PostTradeChecklist';
import { RiskRulesChecklist } from '../components/ui/RiskRulesChecklist';
import { toast } from 'sonner';
import {
    ClipboardCheck,
    ClipboardList,
    ShieldCheck,
    RotateCcw,
    CheckCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryInfo = {
    pre_trade: { 
        icon: ClipboardCheck, 
        color: 'blue',
        gradient: 'from-blue-500 to-blue-600',
        bgColor: 'bg-blue-50',
        textColor: 'text-blue-600',
        label_id: 'Pre-Trade', 
        label_en: 'Pre-Trade',
        desc_id: 'Checklist interaktif sebelum trade',
        desc_en: 'Interactive checklist before trading'
    },
    post_trade: { 
        icon: ClipboardList, 
        color: 'purple',
        gradient: 'from-purple-500 to-purple-600',
        bgColor: 'bg-purple-50',
        textColor: 'text-purple-600',
        label_id: 'Post-Trade', 
        label_en: 'Post-Trade',
        desc_id: 'Review setelah menutup posisi',
        desc_en: 'Review after closing a position'
    },
    risk_rules: { 
        icon: ShieldCheck, 
        color: 'primary',
        gradient: 'from-red-500 to-red-600',
        bgColor: 'bg-red-50',
        textColor: 'text-red-600',
        label_id: 'Risk Rules', 
        label_en: 'Risk Rules',
        desc_id: 'Aturan manajemen risiko',
        desc_en: 'Risk management rules'
    }
};

export const ChecklistPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { t, language, getLocalizedField } = useLanguage();
    const [checklists, setChecklists] = useState([]);
    const [userStatus, setUserStatus] = useState({});
    const [loading, setLoading] = useState(true);

    const currentCategory = searchParams.get('category') || 'pre_trade';

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [checklistsRes, statusRes] = await Promise.all([
                axios.get(`${API}/checklists`),
                axios.get(`${API}/checklists/status`)
            ]);

            setChecklists(checklistsRes.data);
            
            const statusMap = {};
            statusRes.data.forEach(s => {
                statusMap[s.checklist_item_id] = s.checked;
            });
            setUserStatus(statusMap);
        } catch (error) {
            console.error('Error fetching checklists:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCategoryChange = (category) => {
        searchParams.set('category', category);
        setSearchParams(searchParams);
    };

    const handleCheckItem = async (itemId, checked) => {
        // Optimistic update
        setUserStatus(prev => ({
            ...prev,
            [itemId]: checked
        }));

        try {
            await axios.post(`${API}/checklists/status?checklist_item_id=${itemId}&checked=${checked}`);
            if (checked) {
                toast.success(language === 'id' ? 'Item ditandai selesai' : 'Item marked as done', {
                    duration: 1500
                });
            }
        } catch (error) {
            // Revert on error
            setUserStatus(prev => ({
                ...prev,
                [itemId]: !checked
            }));
            toast.error(language === 'id' ? 'Gagal update checklist' : 'Failed to update checklist');
        }
    };

    const resetChecklist = async (category) => {
        const categoryItems = checklists.filter(c => c.category === category);
        
        for (const item of categoryItems) {
            if (userStatus[item.id]) {
                await handleCheckItem(item.id, false);
            }
        }

        toast.success(language === 'id' ? 'Checklist direset' : 'Checklist reset');
    };

    const getCompletionRate = (category) => {
        const categoryItems = checklists.filter(c => c.category === category);
        if (categoryItems.length === 0) return 0;
        const completed = categoryItems.filter(item => userStatus[item.id]).length;
        return Math.round((completed / categoryItems.length) * 100);
    };

    // Get Pre-Trade completion from localStorage
    const getPreTradeCompletion = () => {
        try {
            const saved = localStorage.getItem('preTradeChecklist');
            if (saved) {
                const parsed = JSON.parse(saved);
                const today = new Date().toDateString();
                if (parsed.date === today && parsed.items) {
                    const checkedCount = Object.values(parsed.items).filter(Boolean).length;
                    return Math.round((checkedCount / 18) * 100); // 18 total items
                }
            }
        } catch (e) {
            // Ignore parse errors for localStorage
            console.debug('Could not parse preTradeChecklist from localStorage');
        }
        return 0;
    };

    // Get Post-Trade completion from localStorage
    const getPostTradeCompletion = () => {
        try {
            const saved = localStorage.getItem('postTradeChecklist');
            if (saved) {
                const parsed = JSON.parse(saved);
                const today = new Date().toDateString();
                if (parsed.date === today && parsed.items) {
                    const checkedCount = Object.values(parsed.items).filter(Boolean).length;
                    return Math.round((checkedCount / 17) * 100); // 17 total items
                }
            }
        } catch (e) {
            console.debug('Could not parse postTradeChecklist from localStorage');
        }
        return 0;
    };

    // Get Risk Rules completion from localStorage
    const getRiskRulesCompletion = () => {
        try {
            const saved = localStorage.getItem('riskRulesChecklist');
            if (saved) {
                const parsed = JSON.parse(saved);
                const today = new Date().toDateString();
                if (parsed.date === today && parsed.items) {
                    const checkedCount = Object.values(parsed.items).filter(Boolean).length;
                    return Math.round((checkedCount / 18) * 100); // 18 total items
                }
            }
        } catch (e) {
            console.debug('Could not parse riskRulesChecklist from localStorage');
        }
        return 0;
    };

    if (loading) {
        return (
            <DashboardLayout>
                <div className="space-y-6">
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="h-12 w-full rounded-xl" />
                    <div className="space-y-4">
                        {[1, 2, 3, 4, 5].map((i) => (
                            <Skeleton key={i} className="h-16 rounded-xl" />
                        ))}
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="space-y-8 animate-fade-in">
                {/* Header */}
                <div>
                    <h1 className="text-3xl font-heading font-bold text-slate-900">
                        {t('checklist')}
                    </h1>
                    <p className="text-slate-500 mt-2">
                        {t('checklistDescription')}
                    </p>
                </div>

                {/* Category Cards Overview */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {Object.entries(categoryInfo).map(([key, info]) => {
                        const Icon = info.icon;
                        // Use localStorage progress for all checklist types
                        let progress;
                        if (key === 'pre_trade') {
                            progress = getPreTradeCompletion();
                        } else if (key === 'post_trade') {
                            progress = getPostTradeCompletion();
                        } else if (key === 'risk_rules') {
                            progress = getRiskRulesCompletion();
                        } else {
                            progress = getCompletionRate(key);
                        }
                        const isActive = currentCategory === key;
                        
                        return (
                            <button
                                key={key}
                                onClick={() => handleCategoryChange(key)}
                                className={`glass-card rounded-2xl p-5 text-left transition-all duration-300 ${
                                    isActive 
                                        ? 'ring-2 ring-primary/30 shadow-lg' 
                                        : 'hover:shadow-md'
                                }`}
                                data-testid={`category-${key}`}
                            >
                                <div className="flex items-start justify-between">
                                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${info.gradient} flex items-center justify-center shadow-md`}>
                                        <Icon className="w-6 h-6 text-white" />
                                    </div>
                                    <ProgressRing 
                                        progress={progress}
                                        size={50}
                                        strokeWidth={4}
                                        showPercentage={false}
                                        color={info.color === 'primary' ? 'primary' : info.color}
                                    />
                                </div>
                                <h3 className="font-heading font-semibold text-slate-900 mt-4">
                                    {language === 'id' ? info.label_id : info.label_en}
                                </h3>
                                <p className="text-sm text-slate-500 mt-1">
                                    {language === 'id' ? info.desc_id : info.desc_en}
                                </p>
                                <div className="mt-3 text-sm font-medium text-slate-600">
                                    {progress}% {language === 'id' ? 'selesai' : 'complete'}
                                </div>
                            </button>
                        );
                    })}
                </div>

                {/* Pre-Trade Interactive Checklist */}
                {currentCategory === 'pre_trade' && (
                    <div className="glass-card rounded-2xl p-6 md:p-8">
                        <PreTradeChecklist 
                            onComplete={() => {
                                toast.success(
                                    language === 'id' 
                                        ? '🎯 Anda siap untuk trade dengan disiplin!' 
                                        : '🎯 You are ready to trade with discipline!',
                                    { duration: 3000 }
                                );
                            }}
                        />
                    </div>
                )}

                {/* Post-Trade Interactive Checklist */}
                {currentCategory === 'post_trade' && (
                    <div className="glass-card rounded-2xl p-6 md:p-8">
                        <PostTradeChecklist 
                            onComplete={() => {
                                toast.success(
                                    language === 'id' 
                                        ? '📊 Review selesai. Disiplin membawa kejayaan!' 
                                        : '📊 Review complete. Discipline brings success!',
                                    { duration: 3000 }
                                );
                            }}
                        />
                    </div>
                )}

                {/* Risk Rules Interactive Checklist */}
                {currentCategory === 'risk_rules' && (
                    <div className="glass-card rounded-2xl p-6 md:p-8">
                        <RiskRulesChecklist 
                            onComplete={() => {
                                toast.success(
                                    language === 'id' 
                                        ? '🛡️ Modal terlindung. Trade dengan disiplin!' 
                                        : '🛡️ Capital protected. Trade with discipline!',
                                    { duration: 3000 }
                                );
                            }}
                        />
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
};
